/**
 * Training Edit Page - Create or edit training details
 */

import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { learningApi } from '../api/learningApi'
import type { CreateTrainingInput, UpdateTrainingInput, TrainingType, DeliveryMode } from '../types/learning.types'
import LoadingPage from '@/shared/components/LoadingPage'
import RichTextEditor from '@/shared/components/RichTextEditor'
import { useAuth } from '@/core/auth/AuthContext'

const TRAINING_TYPES: { value: TrainingType; label: string }[] = [
  { value: 'course', label: 'Course' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'certification', label: 'Certification' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'conference', label: 'Conference' },
  { value: 'other', label: 'Other' },
]

const DELIVERY_MODES: { value: DeliveryMode; label: string }[] = [
  { value: 'in_person', label: 'In Person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
]

const CURRENCIES = ['IDR', 'USD', 'SGD', 'EUR', 'GBP']

const TrainingEditPage = () => {
  const { trainingId } = useParams<{ trainingId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Determine if we're in create mode
  const isCreateMode = !trainingId

  // Check if user is admin or HR (required for create mode)
  const isAdminOrHR = user?.roles?.some(r => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  // Form state
  const [formData, setFormData] = useState<UpdateTrainingInput>({
    name: '',
    provider: '',
    category_id: '',
    description: '',
    training_type: 'course',
    is_active: true,
    vendor_name: '',
    vendor_website: '',
    location: '',
    delivery_mode: 'in_person',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    duration_hours: undefined,
    fee: undefined,
    fee_currency: 'IDR',
    max_participants: undefined,
    prerequisites: '',
    target_audience: '',
    learning_objectives: '',
    syllabus: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fetch training details (only in edit mode)
  const { data: training, isLoading } = useQuery({
    queryKey: ['training', trainingId],
    queryFn: () => learningApi.getTraining(trainingId!),
    enabled: !!trainingId && !isCreateMode,
  })

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['trainingCategories'],
    queryFn: () => learningApi.getCategories(),
  })

  // Initialize form with training data
  useEffect(() => {
    if (training) {
      setFormData({
        name: training.name,
        provider: training.provider || '',
        category_id: training.category_id || '',
        description: training.description || '',
        training_type: training.training_type,
        is_active: training.is_active,
        vendor_name: training.vendor_name || '',
        vendor_website: training.vendor_website || '',
        location: training.location || '',
        delivery_mode: training.delivery_mode || 'in_person',
        contact_person: training.contact_person || '',
        contact_email: training.contact_email || '',
        contact_phone: training.contact_phone || '',
        duration_hours: training.duration_hours || undefined,
        fee: training.fee || undefined,
        fee_currency: training.fee_currency || 'IDR',
        max_participants: training.max_participants || undefined,
        prerequisites: training.prerequisites || '',
        target_audience: training.target_audience || '',
        learning_objectives: training.learning_objectives || '',
        syllabus: training.syllabus || '',
      })
    }
  }, [training])

  // Create mutation (for new trainings)
  const createMutation = useMutation({
    mutationFn: (data: CreateTrainingInput) => learningApi.createTraining(data),
    onSuccess: async (newTraining) => {
      await queryClient.invalidateQueries({ queryKey: ['trainings'] })
      navigate(`/learning/trainings/${newTraining.id}`)
    },
  })

  // Update mutation (for existing trainings)
  const updateMutation = useMutation({
    mutationFn: (data: UpdateTrainingInput) => learningApi.updateTraining(trainingId!, data),
    onSuccess: async () => {
      // Wait for cache invalidation to complete before navigating
      await queryClient.invalidateQueries({ queryKey: ['training', trainingId] })
      await queryClient.invalidateQueries({ queryKey: ['trainings'] })
      navigate(`/learning/trainings/${trainingId}`)
    },
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target
    let newValue: string | number | boolean | undefined = value

    if (type === 'number') {
      newValue = value === '' ? undefined : parseFloat(value)
    } else if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked
    }

    setFormData(prev => ({ ...prev, [name]: newValue }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Training name is required'
    }

    if (!formData.duration_hours || formData.duration_hours <= 0) {
      newErrors.duration_hours = 'Duration is required and must be greater than 0'
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format'
    }

    if (formData.vendor_website && !formData.vendor_website.match(/^https?:\/\/.+/)) {
      if (!formData.vendor_website.startsWith('http')) {
        setFormData(prev => ({ ...prev, vendor_website: `https://${prev.vendor_website}` }))
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      if (isCreateMode) {
        // Clean up empty strings for create mode
        const cleanedData: CreateTrainingInput = {
          name: formData.name!,
          provider: formData.provider || undefined,
          category_id: formData.category_id || undefined,
          description: formData.description || undefined,
          training_type: formData.training_type,
          vendor_name: formData.vendor_name || undefined,
          vendor_website: formData.vendor_website || undefined,
          location: formData.location || undefined,
          delivery_mode: formData.delivery_mode,
          contact_person: formData.contact_person || undefined,
          contact_email: formData.contact_email || undefined,
          contact_phone: formData.contact_phone || undefined,
          duration_hours: formData.duration_hours,
          fee: formData.fee,
          fee_currency: formData.fee_currency || 'IDR',
          max_participants: formData.max_participants,
          prerequisites: formData.prerequisites || undefined,
          target_audience: formData.target_audience || undefined,
          learning_objectives: formData.learning_objectives || undefined,
          syllabus: formData.syllabus || undefined,
        }
        createMutation.mutate(cleanedData)
      } else {
        updateMutation.mutate(formData)
      }
    }
  }

  // Redirect non-admin/HR users in create mode
  if (isCreateMode && !isAdminOrHR) {
    navigate('/learning/trainings')
    return null
  }

  if (!isCreateMode && isLoading) return <LoadingPage />

  if (!isCreateMode && !training) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Training not found</h3>
          <Link
            to="/learning/trainings"
            className="text-red-600 hover:text-red-700"
          >
            Back to Training Repository
          </Link>
        </div>
      </div>
    )
  }

  const isPending = isCreateMode ? createMutation.isPending : updateMutation.isPending
  const isError = isCreateMode ? createMutation.isError : updateMutation.isError

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/learning/trainings" className="hover:text-white transition-colors">Trainings</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">{isCreateMode ? 'Add New' : 'Edit'}</span>
          </div>
          <h1 className="text-3xl font-bold text-white">{isCreateMode ? 'Add New Training' : 'Edit Training'}</h1>
          <p className="text-red-200 mt-1">{isCreateMode ? 'Create a new training in the repository' : training?.name}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Training Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <input
                  type="text"
                  name="provider"
                  value={formData.provider || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  name="category_id"
                  value={formData.category_id || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Select category...</option>
                  {categoriesData?.data.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Type</label>
                <select
                  name="training_type"
                  value={formData.training_type || 'course'}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {TRAINING_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active ?? true}
                    onChange={handleChange}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-gray-700">Active</span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          {/* Vendor Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Vendor Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                <input
                  type="text"
                  name="vendor_name"
                  value={formData.vendor_name || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Website</label>
                <input
                  type="text"
                  name="vendor_website"
                  value={formData.vendor_website || ''}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  name="contact_email"
                  value={formData.contact_email || ''}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${errors.contact_email ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.contact_email && <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <input
                  type="text"
                  name="contact_phone"
                  value={formData.contact_phone || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          {/* Course Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Course Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Mode</label>
                <select
                  name="delivery_mode"
                  value={formData.delivery_mode || 'in_person'}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {DELIVERY_MODES.map(mode => (
                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location || ''}
                  onChange={handleChange}
                  placeholder="e.g., Online, Jakarta, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (hours) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="duration_hours"
                  value={formData.duration_hours ?? ''}
                  onChange={handleChange}
                  min="0"
                  step="0.5"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${errors.duration_hours ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.duration_hours && <p className="text-red-500 text-sm mt-1">{errors.duration_hours}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                <input
                  type="number"
                  name="max_participants"
                  value={formData.max_participants ?? ''}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee</label>
                <input
                  type="number"
                  name="fee"
                  value={formData.fee ?? ''}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  name="fee_currency"
                  value={formData.fee_currency || 'IDR'}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {CURRENCIES.map(currency => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Learning Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Learning Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <textarea
                  name="target_audience"
                  value={formData.target_audience || ''}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Who should attend this training?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
                <textarea
                  name="prerequisites"
                  value={formData.prerequisites || ''}
                  onChange={handleChange}
                  rows={2}
                  placeholder="What should attendees know before taking this training?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Learning Objectives</label>
                <RichTextEditor
                  value={formData.learning_objectives || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, learning_objectives: value }))}
                  placeholder="What will attendees learn?"
                  minHeight="120px"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Syllabus</label>
                <RichTextEditor
                  value={formData.syllabus || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, syllabus: value }))}
                  placeholder="Course outline / modules"
                  minHeight="180px"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(isCreateMode ? '/learning/trainings' : `/learning/trainings/${trainingId}`)}
              className="px-6 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-6 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isPending ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {isCreateMode ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCreateMode ? "M12 4v16m8-8H4" : "M5 13l4 4L19 7"} />
                  </svg>
                  {isCreateMode ? 'Create Training' : 'Save Changes'}
                </>
              )}
            </button>
          </div>

          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-700">{isCreateMode ? 'Failed to create training.' : 'Failed to save changes.'} Please try again.</p>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default TrainingEditPage
